import { Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { find } from 'lodash';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import {
  DisplayTable,
  StyledTableCell,
} from '@/components/tables/DisplayTable/DisplayTable';
import { StyledTableRow } from '@/components/tables/DisplayTable/StyledTableRow';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { formatCurrency } from '@/utils/formatting/currency';

import { HypotheticalSaleLoanFormShape } from '../../EstateWaterfallHypotheticalSaleLoanModal.types';
import { SaleLoanIllustration_EstateWaterfallFragment } from '../graphql/SaleLoanIllustration.generated';
import { PROJECTION_PSEUDO_ID } from '../SaleLoanIllustrationContent.utils';
import { getYearEventDescription } from './SaleLoanIllustrationDetails.utils';

export interface SaleLoanIllustrationPaymentScheduleProps {
  waterfall: SaleLoanIllustration_EstateWaterfallFragment | null;
}

export interface PaymentScheduleRow {
  year: number;
  notePayableAtYearStart: Decimal;
  noteInterest: Decimal;
  notePrincipal: Decimal;
  notePayableAtYearEnd: Decimal;
  yearEventDescription?: string;
}

interface GetPaymentScheduleParams {
  termLength: number | null;
  termStartYear: number | null;
  isTwoGrantorHousehold: boolean;
}

function getPaymentSchedule(
  waterfall: SaleLoanIllustration_EstateWaterfallFragment | null,
  { termLength, termStartYear, isTwoGrantorHousehold }: GetPaymentScheduleParams
): PaymentScheduleRow[] {
  if (!waterfall || !termLength) {
    return [];
  }

  const loan = find(
    waterfall.visualizationWithSaleLoanProjections.loans,
    (l) => l.pseudoID === PROJECTION_PSEUDO_ID
  );

  if (!loan) {
    // The projection can omit a loan when its source has no value or its start
    // year is outside the modeled years. An absent loan is not a query failure.
    return [];
  }

  const yearEventDescriptionParams =
    termLength && termStartYear
      ? {
          termStartYear,
          termLengthYears: termLength,
          isTwoGrantorHousehold,
        }
      : null;

  return (
    loan.valueHistory
      .map((vh) => ({
        year: vh.year,
        notePayableAtYearStart: vh.beginningOfYearValue ?? new Decimal(0),
        noteInterest: vh.interestPaid ?? new Decimal(0),
        notePrincipal:
          vh.principalPaid?.times(new Decimal(-1)) ?? new Decimal(0),
        notePayableAtYearEnd: vh.endOfYearValue ?? new Decimal(0),
        yearEventDescription: getYearEventDescription(
          vh.year,
          waterfall,
          yearEventDescriptionParams
        ),
      }))
      // need to limit to term length because the waterfall
      // gives a value for every year up until second death
      .slice(0, termLength + 1)
  );
}

export function SaleLoanIllustrationPaymentSchedule({
  waterfall,
}: SaleLoanIllustrationPaymentScheduleProps) {
  const { control } = useFormContext<HypotheticalSaleLoanFormShape>();
  const { isTwoClientHousehold } = useHouseholdDetailsContext();
  const [termLength, termStartDate] = useWatch({
    control,
    name: ['termLength', 'startDate'],
  });

  const paymentSchedule = useMemo(
    () =>
      getPaymentSchedule(waterfall, {
        termLength: parseInt(termLength || '0', 10),
        termStartYear: termStartDate?.getFullYear() ?? null,
        isTwoGrantorHousehold: isTwoClientHousehold ?? false,
      }),
    [termLength, termStartDate, isTwoClientHousehold, waterfall]
  );

  return (
    <DisplayTable
      columns={[
        { headerName: 'Year', width: '15%' },
        {
          headerName: 'Note payable at year start',
          width: '22.5%',
          align: 'right',
        },
        { headerName: 'Note interest', width: '20%', align: 'right' },
        { headerName: 'Note principal', width: '20%', align: 'right' },
        {
          headerName: 'Note payable at year end',
          width: '22.5%',
          align: 'right',
        },
      ]}
    >
      {paymentSchedule.map((row) => (
        <StyledTableRow key={row.year} sx={{ height: 69 }}>
          <StyledTableCell>
            <Stack>
              <Typography variant="body-xs">{row.year}</Typography>
              {row.yearEventDescription && (
                <Typography variant="body-xs">
                  {row.yearEventDescription}
                </Typography>
              )}
            </Stack>
          </StyledTableCell>
          <StyledTableCell align="right">
            <Typography variant="body-xs">
              {formatCurrency(row.notePayableAtYearStart)}
            </Typography>
          </StyledTableCell>
          <StyledTableCell align="right">
            <Typography variant="body-xs">
              {formatCurrency(row.noteInterest)}
            </Typography>
          </StyledTableCell>
          <StyledTableCell align="right">
            <Typography
              variant="body-xs"
              color={row.notePrincipal.lt(0) ? 'error.main' : undefined}
            >
              {formatCurrency(row.notePrincipal, {
                currencySign: 'accounting',
              })}
            </Typography>
          </StyledTableCell>
          <StyledTableCell align="right">
            <Typography variant="body-xs">
              {formatCurrency(row.notePayableAtYearEnd)}
            </Typography>
          </StyledTableCell>
        </StyledTableRow>
      ))}
      {paymentSchedule.length === 0 && (
        <StyledTableRow>
          <StyledTableCell colSpan={5}>
            <Typography variant="body-xs">
              Schedule will generate when all required fields are specified
            </Typography>
          </StyledTableCell>
        </StyledTableRow>
      )}
    </DisplayTable>
  );
}
